// src/components/UserSettings.js
import React, { useState, useEffect } from 'react';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const titleOptions = [
  { value: '', label: 'Select a title (optional)' },
  { value: 'ABCS-chair', label: 'ABCS-chair' },
  { value: 'ABFCS-chair', label: 'ABFCS-chair' },
  { value: 'AACS-chair', label: 'AACS-chair' },
  { value: 'ABCS-committee', label: 'ABCS-committee member' },
  { value: 'ABFCS-committee', label: 'ABFCS-committee member' },
  { value: 'AACS-committee', label: 'AACS-committee member' },
  { value: 'fellowship-director', label: 'Fellowship Director' },
  { value: 'ABCS-staff', label: 'ABCS-staff' },
  { value: 'AACS-staff', label: 'AACS-staff' },
  { value: 'ABFCS-staff', label: 'ABFCS-staff' }
];

const UserSettings = ({ onClose }) => {
  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [receiveTextAlerts, setReceiveTextAlerts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const auth = getAuth();
  const db = getFirestore();
  
  // Load user data on component mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        
        // Load Firestore user data
        const userRef = doc(db, 'examContentUsers', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setDisplayName(user.displayName || '');
          setTitle(userData.title || '');
          setPhoneNumber(userData.phoneNumber || '');
          setAvatarUrl(userData.photoURL || user.photoURL || '');
          setReceiveTextAlerts(userData.receiveTextAlerts || false);
        }
      } catch (err) {
        console.error('Error loading user data', err);
        setError('Failed to load user settings');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [auth, db]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSaving(true);
    
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      // Update auth profile
      await updateProfile(user, {
        displayName: displayName,
        photoURL: avatarUrl
      });
      
      // Update Firestore user data
      const userRef = doc(db, 'examContentUsers', user.uid);
      await updateDoc(userRef, {
        displayName: displayName,
        title: title,
        phoneNumber: phoneNumber,
        photoURL: avatarUrl,
        receiveTextAlerts: receiveTextAlerts,
        updatedAt: new Date()
      });
      
      setSuccess('Settings updated successfully');
      
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error updating user settings', err);
      setError('Failed to update settings: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="user-settings-modal">
        <div className="user-settings-content">
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="user-settings-modal">
      <div className="user-settings-content">
        <div className="user-settings-header">
          <h2>User Settings</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="user-settings-form">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <div className="form-group">
            <label htmlFor="settings-displayName">Name</label>
            <input
              id="settings-displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
              disabled={isSaving}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="settings-title">Title</label>
            <select
              id="settings-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isSaving}
              className="form-select"
            >
              {titleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="settings-phoneNumber">Mobile Phone</label>
            <input
              id="settings-phoneNumber"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="(123) 456-7890"
              disabled={isSaving}
            />
          </div>
          
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={receiveTextAlerts}
                onChange={(e) => setReceiveTextAlerts(e.target.checked)}
                disabled={isSaving || !phoneNumber}
              />
              <span>Receive text alerts</span>
            </label>
            {!phoneNumber && receiveTextAlerts && (
              <p className="helper-text">Enter a phone number to enable text alerts</p>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="settings-avatarUrl">Profile Image URL</label>
            <input
              id="settings-avatarUrl"
              type="url"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://example.com/your-photo.jpg"
              disabled={isSaving}
            />
          </div>
          
          {avatarUrl && (
            <div className="avatar-preview">
              <img src={avatarUrl} alt="Profile Preview" />
            </div>
          )}
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="save-button"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button 
              type="button" 
              className="cancel-button"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserSettings;